/**
 * Le forfait d'une tournée (lot U4, ADR-0016) : un montant HT facturé à CHAQUE occurrence, en plus de la main d'œuvre et du
 * déplacement — les trois se cumulent, ou un seul des trois.
 *
 * DONNÉE FINANCIÈRE (affichage seulement : l'API décide) : ce composant n'est monté, et n'appelle l'API, que pour qui voit les
 * prix ; un Responsable ou un Planificateur n'en voit aucune trace. Écrire exige en plus le droit d'écrire les prix ET de gérer
 * le planning. Le forfait se LIT de la tournée : l'intervention ne le recopie pas, son chiffrage le lit.
 *
 * Un refus de concurrence (409 : la tournée a changé) relit le forfait PUIS se dit, jamais l'inverse.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type ForfaitTourneeVue } from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import {
  corpsForfait,
  forfaitInchange,
  lireForfait,
  phraseForfait,
  phraseResultatForfait,
  saisieDepuisForfait,
  type SaisieForfait,
} from '../domaine/forfait-tournee';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function ForfaitTournee({
  tourId,
  arretee,
  peutGerer,
  onChange,
}: {
  tourId: string;
  arretee: boolean;
  /** Gérer le planning (le droit d'écrire les prix s'ajoute, lu ici). */
  peutGerer: boolean;
  /** Après une écriture : la page relit le briefing (la révision de la tournée a avancé, le chiffrage a changé). */
  onChange: () => void;
}) {
  const droits = useDroitsGammes();
  const [forfait, setForfait] = useState<ForfaitTourneeVue | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<SaisieForfait | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [succes, setSucces] = useState<string | null>(null);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!droits.peutVoirPrix) return;
    let annule = false;
    api
      .forfaitTournee(tourId)
      .then((f) => {
        if (annule) return;
        setForfait(f);
        setErreurLecture(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurLecture(e instanceof ApiError ? e.message : 'Lecture du forfait impossible.');
      });
    return () => {
      annule = true;
    };
  }, [droits.peutVoirPrix, tourId]);

  if (!droits.peutVoirPrix) return null;
  const peutEcrire = droits.peutEcrirePrix && peutGerer && !arretee;

  /**
   * Le forfait se RELIT à l'ouverture du panneau : le jeton est celui de la tournée, qui avance aussi quand on la corrige
   * (remarque, machines…) : un jeton gardé depuis l'affichage serait périmé et l'enregistrement échouerait pour rien.
   */
  const ouvrir = async () => {
    setErreur(null);
    setSucces(null);
    try {
      const f = await api.forfaitTournee(tourId);
      setForfait(f);
      setSaisie(saisieDepuisForfait(f.forfait));
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Lecture du forfait impossible.'));
    }
  };

  const enregistrer = async () => {
    if (!forfait || !saisie) return;
    const lu = lireForfait(saisie);
    if (!lu.ok) {
      setErreur(new Error(lu.erreur));
      return;
    }
    if (forfaitInchange(forfait.forfait, lu)) {
      setSaisie(null);
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      const f = await api.definirForfaitTournee(tourId, corpsForfait(forfait.revision, lu));
      setForfait(f);
      setSaisie(null);
      setSucces(phraseResultatForfait(f.forfait));
      onChange();
    } catch (e) {
      const err = e instanceof ApiError || e instanceof Error ? e : new Error('Enregistrement impossible.');
      if (err instanceof ApiError && err.status === 409) {
        // On relit AVANT de dire le refus : la saisie repart du forfait à jour.
        setSaisie(null);
        setForfait(await api.forfaitTournee(tourId).catch(() => forfait));
        onChange();
      }
      setErreur(err);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="forfait-tournee">
      <h3>Forfait de la tournée</h3>
      {erreurLecture && <div className="message erreur">{erreurLecture}</div>}
      {!forfait && !erreurLecture && <p className="aide">Lecture du forfait…</p>}
      {forfait && (
        <>
          <p>{phraseForfait(forfait.forfait)}</p>
          <p className="aide">
            Les trois se cumulent : forfait, main d’œuvre et déplacement, ou un seul des trois. Le forfait se lit de la tournée, il
            est compté une seule fois par passage.
          </p>
          {peutEcrire && (
            <div className="saisie-ligne planning-actions" role="group" aria-label="Gestes sur le forfait">
              <button type="button" className="secondaire" disabled={enCours} onClick={() => void ouvrir()}>
                {forfait.forfait === null ? 'Poser un forfait' : 'Modifier le forfait'}
              </button>
            </div>
          )}
        </>
      )}
      {succes && <div className="message succes">{succes}</div>}
      {!saisie && <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />}

      {saisie && forfait && (
        <Panneau
          titre="Forfait de la tournée"
          sousTitre="Un montant HT facturé à chaque occurrence, en plus de la main d’œuvre et du déplacement. Videz le montant pour retirer le forfait."
          onFermer={() => setSaisie(null)}
          actions={
            <>
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setSaisie(null)}>
                Ne rien changer
              </button>
              <button type="button" disabled={enCours} onClick={() => void enregistrer()}>
                Enregistrer
              </button>
            </>
          }
        >
          <div className="champ">
            <label htmlFor="forfait-libelle">Libellé (facultatif)</label>
            <input
              id="forfait-libelle"
              type="text"
              maxLength={120}
              value={saisie.libelle}
              placeholder="Visite mensuelle"
              onChange={(e) => setSaisie({ ...saisie, libelle: e.target.value })}
            />
          </div>
          <div className="champ">
            <label htmlFor="forfait-montant">Montant HT par occurrence (€)</label>
            <input
              id="forfait-montant"
              type="text"
              inputMode="decimal"
              value={saisie.montantTexte}
              placeholder="450,00"
              onChange={(e) => setSaisie({ ...saisie, montantTexte: e.target.value })}
            />
          </div>
          <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
        </Panneau>
      )}
    </div>
  );
}
